import React, { useState } from "react";
import {
  Modal,
  StyleSheet,
  View,
  TouchableOpacity,
  ActivityIndicator,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { ThemedText } from "@/components/ThemedText";
import { ThemedView } from "@/components/ThemedView";
import { ThemedInput } from "@/components/ThemedInput";
import { ThemedButton } from "@/components/ThemedButton";
import { CustomAlert } from "@/components/ThemedAlert";
import { useThemeColor } from "@/hooks/useThemeColor";
import { useFirebase } from "@/components/firebase/useFirebase";
import { addUserFunc, addServiceUserFunc } from "@/services/firebase/user-functions";
import { AddUserArgs, AppUserModel, NormalUserClaims, ServiceUserClaims, validatePasswordPolicy } from "common247";

/**
 * Props for the CreateUserModal component.
 */
export interface ICreateUserModalProps {
  /** Whether the modal is currently visible. */
  visible: boolean;
  /** Callback fired when the modal should close. */
  onClose: () => void;
  /** Optional callback fired after a user is successfully created. */
  onUserCreated?: (user: AppUserModel) => void;
  /** Initial account type to create (defaults to 'user'). */
  initialAccountType?: "user" | "serviceaccount";
}

/**
 * Modal dialog for creating a standard normal user account with email and password.
 * Used by administrators to provision test accounts (e.g. for Google Play Console review).
 *
 * @param {ICreateUserModalProps} props - The component props.
 * @returns {React.ReactElement | null} The rendered modal component.
 */
export function CreateUserModal({ visible, onClose, onUserCreated, initialAccountType = "user" }: ICreateUserModalProps): React.ReactElement | null {
  const services = useFirebase();

  const [accountType, setAccountType] = useState<"user" | "serviceaccount">(initialAccountType);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const [nameError, setNameError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const cardBg = useThemeColor({}, "card");
  const borderColor = useThemeColor({}, "border");
  const tintColor = useThemeColor({}, "tint");
  const textMuted = useThemeColor({}, "textMuted");

  React.useEffect(() => {
    if (visible) {
      setAccountType(initialAccountType);
    }
  }, [visible, initialAccountType]);

  /**
   * Resets all input and error states.
   */
  const resetForm = (): void => {
    setName("");
    setEmail("");
    setUsername("");
    setPassword("");
    setShowPassword(false);
    setNameError("");
    setEmailError("");
    setPasswordError("");
    setIsSubmitting(false);
  };

  /**
   * Handles modal dismissal and form reset.
   */
  const handleClose = (): void => {
    if (isSubmitting) return;
    resetForm();
    onClose();
  };

  /**
   * Validates form inputs against application rules and password policy.
   * @returns {boolean} True if all fields are valid.
   */
  const validateForm = (): boolean => {
    let isValid = true;

    // Validate Name
    const trimmedName = name.trim();
    if (!trimmedName) {
      setNameError("Display name is required.");
      isValid = false;
    } else {
      setNameError("");
    }

    // Validate Email
    const trimmedEmail = email.trim().toLowerCase();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!trimmedEmail) {
      setEmailError("Email address is required.");
      isValid = false;
    } else if (!emailRegex.test(trimmedEmail)) {
      setEmailError("Please enter a valid email address.");
      isValid = false;
    } else if (accountType === "serviceaccount" && !trimmedEmail.endsWith("@on247.net")) {
      setEmailError("Service account email must end with @on247.net.");
      isValid = false;
    } else {
      setEmailError("");
    }

    // Validate Password
    const passwordValidation = validatePasswordPolicy(password);
    if (!passwordValidation.isValid) {
      setPasswordError(passwordValidation.errorMessage || "Password does not meet requirements.");
      isValid = false;
    } else {
      setPasswordError("");
    }

    return isValid;
  };

  /**
   * Submits the new user creation request via addUserFunc.
   */
  const handleSubmit = async (): Promise<void> => {
    if (!services) {
      CustomAlert.alert("Error", "Firebase services are not initialized.");
      return;
    }

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      const cleanEmail = email.trim().toLowerCase();
      const cleanName = name.trim();
      const cleanUsername = username.trim().toLowerCase() || cleanEmail;

      let createdUser: AppUserModel;
      if (accountType === "serviceaccount") {
        createdUser = await addServiceUserFunc(services, {
          email: cleanEmail,
          name: cleanName,
          password: password,
          claimSet: ServiceUserClaims,
        });

        CustomAlert.alert(
          "Service User Created",
          `Service account for ${createdUser.email || cleanEmail} was created successfully.`,
        );
      } else {
        const args: AddUserArgs = {
          email: cleanEmail,
          name: cleanName,
          username: cleanUsername,
          password: password,
          claimSet: NormalUserClaims,
        };

        createdUser = await addUserFunc(services, args);

        CustomAlert.alert(
          "User Created",
          `Normal user account for ${createdUser.email || cleanEmail} was created successfully.`,
        );
      }

      resetForm();
      onClose();

      if (onUserCreated) {
        onUserCreated(createdUser);
      }
    } catch (error: any) {
      console.error("CreateUserModal failed to create user:", error);
      CustomAlert.alert(
        "Creation Failed",
        error.message || "Failed to create user account. Please check the logs.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!visible) return null;

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={handleClose}
    >
      <KeyboardAvoidingView
        behavior={Platform.OS === "ios" ? "padding" : "height"}
        style={styles.overlay}
      >
        <TouchableOpacity
          style={styles.backdrop}
          activeOpacity={1}
          onPress={handleClose}
        />
        <ThemedView
          style={[styles.dialog, { backgroundColor: cardBg, borderColor }]}
          role="form"
          accessibilityRole="form"
          {...(Platform.OS === "web" ? { onSubmit: (e: any) => e.preventDefault() } : {})}
        >
          <ScrollView
            contentContainerStyle={styles.scrollContent}
            keyboardShouldPersistTaps="handled"
            showsVerticalScrollIndicator={false}
          >
            {/* Header */}
            <View style={styles.header}>
              <View style={[styles.iconBadge, { backgroundColor: tintColor + "1A" }]}>
                <Ionicons name="person-add" size={24} color={tintColor} />
              </View>
              <View style={styles.headerTextContainer}>
                <ThemedText type="subtitle" style={styles.title}>
                  {accountType === "serviceaccount" ? "Create Service Account" : "Create Normal User"}
                </ThemedText>
                <ThemedText style={[styles.description, { color: textMuted }]}>
                  {accountType === "serviceaccount"
                    ? "Provision an internal service account for automated background tasks and CI/CD."
                    : "Create a standard user account with email and password for Google Play review or testing."}
                </ThemedText>
              </View>
            </View>

            {/* Account Type Toggle */}
            <View style={styles.inputGroup}>
              <ThemedText style={styles.label}>Account Type</ThemedText>
              <View style={styles.typeSelectorRow}>
                <TouchableOpacity
                  style={[
                    styles.typeSelectorBtn,
                    accountType === "user" && { backgroundColor: tintColor, borderColor: tintColor },
                  ]}
                  onPress={() => setAccountType("user")}
                  disabled={isSubmitting}
                >
                  <ThemedText
                    style={[
                      styles.typeSelectorText,
                      accountType === "user" ? { color: "#fff", fontWeight: "700" } : { color: textMuted },
                    ]}
                  >
                    Normal User
                  </ThemedText>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[
                    styles.typeSelectorBtn,
                    accountType === "serviceaccount" && { backgroundColor: "#f0a500", borderColor: "#f0a500" },
                  ]}
                  onPress={() => setAccountType("serviceaccount")}
                  disabled={isSubmitting}
                >
                  <ThemedText
                    style={[
                      styles.typeSelectorText,
                      accountType === "serviceaccount" ? { color: "#fff", fontWeight: "700" } : { color: textMuted },
                    ]}
                  >
                    Service Account
                  </ThemedText>
                </TouchableOpacity>
              </View>
            </View>

            {/* Display Name Input */}
            <View style={styles.inputGroup}>
              <ThemedText style={styles.label}>Display Name</ThemedText>
              <ThemedInput
                placeholder="e.g. Google Play Reviewer"
                value={name}
                onChangeText={(val) => {
                  setName(val);
                  if (nameError) setNameError("");
                }}
                error={nameError}
                editable={!isSubmitting}
                autoCorrect={false}
              />
            </View>

            {/* Email Address Input */}
            <View style={styles.inputGroup}>
              <ThemedText style={styles.label}>Email Address</ThemedText>
              <ThemedInput
                placeholder="e.g. reviewer@example.com"
                value={email}
                onChangeText={(val) => {
                  setEmail(val);
                  if (emailError) setEmailError("");
                }}
                error={emailError}
                keyboardType="email-address"
                autoCapitalize="none"
                autoCorrect={false}
                editable={!isSubmitting}
              />
            </View>

            {/* Username Input (Optional) */}
            <View style={styles.inputGroup}>
              <ThemedText style={styles.label}>Username (Optional)</ThemedText>
              <ThemedInput
                placeholder="Defaults to email if blank"
                value={username}
                onChangeText={(val) => setUsername(val)}
                autoCapitalize="none"
                autoCorrect={false}
                editable={!isSubmitting}
              />
            </View>

            {/* Password Input */}
            <View style={styles.inputGroup}>
              <View style={styles.passwordLabelRow}>
                <ThemedText style={styles.label}>Password</ThemedText>
                <TouchableOpacity
                  onPress={() => setShowPassword(!showPassword)}
                  style={styles.showPasswordBtn}
                  hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                >
                  <Ionicons
                    name={showPassword ? "eye-off-outline" : "eye-outline"}
                    size={16}
                    color={textMuted}
                  />
                  <ThemedText style={[styles.showPasswordText, { color: textMuted }]}>
                    {showPassword ? "Hide" : "Show"}
                  </ThemedText>
                </TouchableOpacity>
              </View>
              <ThemedInput
                placeholder="Min 6 chars (upper, lower, number, symbol)"
                value={password}
                onChangeText={(val) => {
                  setPassword(val);
                  if (passwordError) setPasswordError("");
                }}
                secureTextEntry={!showPassword}
                error={passwordError}
                editable={!isSubmitting}
                autoCapitalize="none"
                autoCorrect={false}
                autoComplete="new-password"
                textContentType="newPassword"
              />
              <ThemedText style={[styles.passwordHint, { color: textMuted }]}>
                Must contain at least 6 characters, including uppercase, lowercase, a digit, and a special character.
              </ThemedText>
            </View>

            {/* Action Buttons */}
            <View style={styles.buttonRow}>
              <ThemedButton
                title="Cancel"
                variant="minimal"
                onPress={handleClose}
                disabled={isSubmitting}
                style={styles.cancelBtn}
              />
              <ThemedButton
                title={isSubmitting ? "Creating..." : "Create User"}
                variant="primary"
                onPress={handleSubmit}
                disabled={isSubmitting}
                loading={isSubmitting}
                style={styles.submitBtn}
              />
            </View>

            {isSubmitting && (
              <View style={styles.loadingContainer}>
                <ActivityIndicator size="small" color={tintColor} />
                <ThemedText style={[styles.loadingText, { color: textMuted }]}>
                  Calling addUserFunc API...
                </ThemedText>
              </View>
            )}
          </ScrollView>
        </ThemedView>
      </KeyboardAvoidingView>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.55)",
    justifyContent: "center",
    alignItems: "center",
    padding: 16,
  },
  backdrop: {
    ...StyleSheet.absoluteFill,
  },
  dialog: {
    width: "100%",
    maxWidth: 480,
    borderRadius: 16,
    borderWidth: 1,
    overflow: "hidden",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.2,
    shadowRadius: 16,
    elevation: 8,
  },
  scrollContent: {
    padding: 20,
    gap: 16,
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    marginBottom: 4,
  },
  iconBadge: {
    width: 44,
    height: 44,
    borderRadius: 12,
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  headerTextContainer: {
    flex: 1,
    gap: 2,
  },
  title: {
    fontSize: 18,
  },
  description: {
    fontSize: 12,
    lineHeight: 16,
  },
  inputGroup: {
    gap: 4,
  },
  label: {
    fontSize: 13,
    fontWeight: "600",
  },
  passwordLabelRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  showPasswordBtn: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  showPasswordText: {
    fontSize: 12,
  },
  passwordHint: {
    fontSize: 11,
    lineHeight: 14,
    marginTop: 2,
  },
  buttonRow: {
    flexDirection: "row",
    justifyContent: "flex-end",
    gap: 10,
    marginTop: 8,
  },
  cancelBtn: {
    minWidth: 90,
  },
  submitBtn: {
    minWidth: 120,
  },
  loadingContainer: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    marginTop: 8,
  },
  loadingText: {
    fontSize: 12,
  },
  typeSelectorRow: {
    flexDirection: "row",
    gap: 8,
    marginTop: 4,
  },
  typeSelectorBtn: {
    flex: 1,
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "rgba(0,0,0,0.15)",
    alignItems: "center",
    justifyContent: "center",
  },
  typeSelectorText: {
    fontSize: 12,
  },
});
